import { EnfinitySecondaryBtn } from "@/components/design-system/enfinity/EnfinityButton";
import { Slider } from "@/components/ui/slider";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { ArrowRight } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

export default function HowMuch() {
  const { ref, isVisible } = useScrollReveal();
  const [dailyViews, setDailyViews] = useState(79000);
  const [inputValue, setInputValue] = useState("79000");
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputRef = useRef(null);

  // Calculate revenue based on $7.50 per 1000 views
  const revenuePer1000Views = 7.5;

  const monthlyRevenue = useMemo(() => {
    const monthlyViews = dailyViews * 30;
    return (monthlyViews / 1000) * revenuePer1000Views;
  }, [dailyViews]);

  const yearlyRevenue = useMemo(() => {
    const yearlyViews = dailyViews * 365;
    return (yearlyViews / 1000) * revenuePer1000Views;
  }, [dailyViews]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-US").format(num);
  };

  // Slider range
  const minViews = 1000;
  const maxViews = 1000000;

  const handleSliderChange = (value) => {
    setDailyViews(value[0]);
    setInputValue(value[0].toString());
  };

  const handleInputChange = (e) => {
    // Allow numbers and commas while typing - keep exactly what user types
    const value = e.target.value.replace(/[^0-9,]/g, "");
    setInputValue(value);

    // Remove commas to get numeric value for real-time calculation
    const rawValue = value.replace(/,/g, "");

    if (rawValue === "") {
      return; // Let user clear the field while typing
    }

    const numValue = parseInt(rawValue, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      // Update dailyViews for real-time calculation (slider and revenue update)
      // Don't modify inputValue - let user type freely including commas
      const clampedValue = Math.max(minViews, Math.min(maxViews, numValue));
      setDailyViews(clampedValue);
    }
  };

  const handleInputFocus = (e) => {
    setIsInputFocused(true);
    // Show raw number without commas when focused for easier editing
    setInputValue(dailyViews.toString());
    // Select all text when focused for easier editing
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.select();
      }
    }, 0);
  };

  const handleInputBlur = () => {
    setIsInputFocused(false);
    // When user leaves the input, ensure it's within bounds
    const rawValue = inputValue.replace(/,/g, "");
    const currentValue = parseInt(rawValue, 10);
    let finalValue = dailyViews;

    if (!isNaN(currentValue) && rawValue !== "") {
      finalValue = Math.max(minViews, Math.min(maxViews, currentValue));
    } else {
      finalValue =
        dailyViews < minViews
          ? minViews
          : dailyViews > maxViews
            ? maxViews
            : dailyViews;
    }

    setDailyViews(finalValue);
    setInputValue(finalValue.toString());
  };

  return (
    <div
      id="how-much-shield"
      ref={ref}
      className={`${isVisible ? "" : ""} py-20 lg:py-32 px-4 lg:px-12 xl:px-12`}
      style={{
        background:
          "linear-gradient(-56.6593deg, rgb(139, 92, 246) 0%, rgb(153, 116, 248) 50.139%, rgb(167, 139, 250) 100%)",
      }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Main Heading */}
        <h1
          className="text-5xl xxs:text-[55px] lg:text-[80px] 4xl:text-[80px] font-normal text-white text-center leading-normal mb-6 enfinity-header-font"
          style={{ lineHeight: "1.2" }}
        >
          How much value are you missing?
        </h1>

        {/* Subtitle */}
        <p className="text-xl xxs:text-[1rem] lg:text-[24px] font-normal text-white text-center leading-normal mb-12 max-w-[957px] mx-auto enfinity-subtext-font">
          Recover the true value of your content. Estimate how much stolen or
          re-uploaded views could you be earning with EnfiniShield
        </p>

        {/* Main White Card */}
        <div className="bg-white rounded-[20px] h-auto min-h-[488px] px-6 lg:px-[53px] py-[53px] flex flex-col lg:flex-row gap-8 lg:gap-0">
          {/* Left Section */}
          <div className="flex-1 flex flex-col lg:pr-8">
            <h2 className="text-[36px] lg:text-[36px] font-normal text-[#8b5cf6] mb-4 lg:mb-6 4xl:text-[40px] enfinity-header-font">
              How many views are being taken?
            </h2>
            <p className="text-[18px] xxs:text-[1rem] lg:text-[16px] 4xl:text-[16px] font-normal text-black leading-[25px] lg:leading-[22px] mb-20 4xl:mb-16 lg:mb-20 max-w-[521px] lg:max-w-[480px] enfinity-subtext-font">
              Drag the slider to see how much estimated lost revenue you could
              recover from unauthorized uploads.
            </p>

            {/* Slider Container */}
            <div className="relative w-full max-w-[508px] mt-4 mb-12 xl:-mt-4 1xl:-mt-0 4xl:-mt-[-13px]">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[24px] font-normal text-black enfinity-header-font">
                  Daily Views
                </p>
                <input
                  ref={inputRef}
                  type="text"
                  value={isInputFocused ? inputValue : formatNumber(dailyViews)}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  className="text-[24px] font-normal text-black text-right enfinity-header-font bg-transparent border-b-2 border-[#8b5cf6] focus:outline-none focus:border-[#8b5cf6] w-32"
                />
              </div>

              {/* Slider */}
              <div className="mb-2">
                <Slider
                  value={[dailyViews]}
                  onValueChange={handleSliderChange}
                  min={minViews}
                  max={maxViews}
                  step={1000}
                  className="[&_[data-slot=slider-track]]:h-[19px] [&_[data-slot=slider-track]]:bg-[rgba(139,92,246,0.1)] [&_[data-slot=slider-range]]:bg-[#8b5cf6] [&_[role=slider]]:bg-[#8b5cf6] [&_[role=slider]]:border-2 [&_[role=slider]]:border-white [&_[role=slider]]:shadow-md [&_[role=slider]]:w-5 [&_[role=slider]]:h-5"
                />
              </div>

              {/* Slider Labels */}
              <div className="flex items-center justify-between text-[14px] font-normal text-black opacity-50 enfinity-subtext-font">
                <p className="text-right">{formatNumber(1000)}</p>
                <p className="text-center">{formatNumber(500000)}</p>
                <p className="text-right">{formatNumber(1000000)}</p>
              </div>
            </div>

            {/* Bottom Text - aligned with right container bottom */}
            <p className="text-[16px] xxs:text-[1rem] font-normal text-black opacity-50 leading-[25px] max-w-[567px] enfinity-subtext-font mt-auto">
              Stop letting others profit from your work. Let EnfiniShield
              protect your content and earnings across platforms.
            </p>
          </div>

          {/* Right Section */}
          <div className="lg:w-[470px] flex-shrink-0 bg-[rgba(139,92,246,0.1)] border border-[#8b5cf6] rounded-[10px] p-[33px] flex flex-col">
            <h2 className="text-[24px] xxs:text-[20px] xs:text-[24px] sm:text-[26px] md:text-[28px] lg:text-[26px] 4xl:text-[28px] font-normal text-[#8b5cf6] leading-[34px] mb-6 enfinity-header-font enfinity-header-font">
              How much are you missing?
            </h2>

            {/* Monthly Revenue Card */}
            <div className="bg-white rounded-[10px] px-5 py-5 mb-4 flex flex-col gap-[11px]">
              <p className="text-[14px] xxs:text-[13px] xs:text-[13px] sm:text-[14px] md:text-[18px] lg:text-[16px] 4xl:text-[16px] font-normal text-[rgba(0,0,0,0.5)] enfinity-subtext-font enfinity-subtext-font">
                MONTHLY LOST REVENUE
              </p>
              <p className="text-[40px] xxs:text-[15px] xs:text-[19px] sm:text-[18px] md:text-[28px] lg:text-[26px] 4xl:text-[28px] font-semibold text-black enfinity-header-font">
                {formatCurrency(monthlyRevenue)}
              </p>
            </div>

            {/* Yearly Revenue Card */}
            <div className="bg-white rounded-[10px] px-5 py-3 mb-6 flex flex-col gap-[11px]">
              <p className="text-[14px] xxs:text-[11px] xs:text-[13px] sm:text-[13px] md:text-[18px] lg:text-[14px] 4xl:text-[14px] font-normal text-[rgba(0,0,0,0.5)] enfinity-subtext-font enfinity-subtext-font">
                YEARLY LOST REVENUE
              </p>
              <p className="text-[24px] xxs:text-[13px] xs:text-[17px] sm:text-[15px] md:text-[28px] lg:text-[25px] 4xl:text-[24px] font-medium text-black enfinity-header-font">
                {formatCurrency(yearlyRevenue)}
              </p>
            </div>

            {/* Bottom Text - rate and disclaimer */}
            <div className="mt-auto space-y-1">
              <p className="text-[20px] xxs:text-[11px] xs:text-[15px] sm:text-[18px] md:text-[20px] lg:text-[20px] 4xl:text-[20px] font-normal text-black enfinity-subtext-font">
                Based on $7.50 per 1000 views
              </p>
              <p className="text-sm font-normal text-black enfinity-subtext-font">
                Disclaimer: Revenue estimates are projections only and not
                guaranteed.
              </p>
            </div>
          </div>
        </div>

        {/* Get Started Button */}
        <div className="flex justify-center mt-16">
          <Link to="/our-solutions/enfinishield/esign">
            <EnfinitySecondaryBtn className="flex items-center gap-2">
              <span>Get started with EnfiniShield</span>
              <ArrowRight className="w-4 h-4" />
            </EnfinitySecondaryBtn>
          </Link>
        </div>
      </div>
    </div>
  );
}
